import { OrganizationSchema, PageHead, SoftwareAppSchema, WebsiteSchema } from '@/shared/seo';
import { MEMBERSHEEP_APP_STORE_URL } from '@/membersheep/config';
import { DOWNLOAD_LINKS } from '@/travo/config';
import { APP_IDS, SITE_URL, SUPPORT_EMAIL } from '@/shared/site';
import AppTiles from './AppTiles';
import LabsShell from './LabsShell';

const DESCRIPTION =
  'Travo Labs is an independent studio in Taiwan making Travo, the travel tracker and trip planner, and MemberSheep, the membership card wallet for iPhone.';

const PRINCIPLES = [
  {
    title: 'Yours, not ours',
    body: "Apps work without handing us more than they need. MemberSheep doesn't even have an account."
  },
  {
    title: 'Small and finished',
    body: 'Each app does one job and does it well, rather than many jobs halfway.'
  },
  {
    title: 'Made in Taiwan',
    body: 'Built for travellers and shoppers here first, in their own language.'
  }
];

/** www.travolabs.com/ — the studio and its apps. English only. */
const LabsPage = () => (
  <LabsShell>
    <PageHead
      title="Travo Labs: Independent Apps for iPhone and Android"
      description={DESCRIPTION}
      path="/"
      siteName="Travo Labs"
      ogLocale="en_US"
      image={`${SITE_URL}/og-image-labs.png`}
      imageAlt="Travo Labs: for the places you go and the cards you carry. The Travo and MemberSheep apps."
      themeColor="#f5f5f7"
    />
    <OrganizationSchema />
    <WebsiteSchema />
    {/* Both apps in short; each app's home page carries its full entity under the same id */}
    <SoftwareAppSchema
      id={APP_IDS.travo}
      name="Travo"
      url={`${SITE_URL}/travo`}
      description="Free travel tracker, passport and trip planner: trips start on their own, every city and country is logged, and each new country stamps a passport."
      operatingSystem="iOS, Android"
      applicationCategory="TravelApplication"
      inLanguage={['en', 'zh-Hant-TW', 'ja', 'ko', 'th']}
      downloadUrl={[DOWNLOAD_LINKS.appStore, DOWNLOAD_LINKS.playStore]}
    />
    <SoftwareAppSchema
      id={APP_IDS.memberSheep}
      name="MemberSheep"
      url={`${SITE_URL}/membersheep`}
      description="Membership card wallet for iPhone: scan a card once and show it at the till in a tap. No account; cards stay on the device."
      operatingSystem="iOS 17 or later"
      applicationCategory="UtilitiesApplication"
      inLanguage={['en', 'zh-Hant-TW']}
      downloadUrl={MEMBERSHEEP_APP_STORE_URL ? [MEMBERSHEEP_APP_STORE_URL] : undefined}
    />

    <main className="mx-auto max-w-[1120px] px-4 md:px-6">
      <section className="grid items-end gap-5 pt-14 pb-10 md:grid-cols-[1.5fr_1fr] md:gap-12 md:pt-[104px] md:pb-16">
        <h1 className="font-display text-[clamp(44px,7.4vw,96px)] leading-[0.94] font-extrabold tracking-[-0.045em] motion-safe:animate-rise">
          For the places you go <span className="text-sys-secondary">and the cards you carry.</span>
        </h1>
        <p className="max-w-[32ch] text-[19px] text-sys-secondary motion-safe:animate-rise motion-safe:[animation-delay:80ms]">
          Travo Labs is an independent studio in Taiwan making small, careful apps that keep your data yours.
        </p>
      </section>

      <AppTiles />

      <section className="mt-[72px] grid gap-6 border-t border-sys-separator pt-10 md:mt-28 md:grid-cols-[1fr_1.4fr] md:gap-12">
        <h2 className="font-display text-[28px] leading-tight font-bold tracking-[-0.02em]">How we build</h2>
        <div className="grid gap-[18px] md:grid-cols-3 md:gap-7">
          {PRINCIPLES.map((item) => (
            <div key={item.title}>
              <h3 className="font-display text-[17px] font-bold">{item.title}</h3>
              <p className="mt-1.5 text-[15px] text-sys-secondary">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        id="contact"
        className="mt-[72px] flex flex-wrap items-center justify-between gap-5 rounded-3xl border border-sys-separator bg-sys-surface px-8 py-7"
      >
        <div>
          <h2 className="font-display text-[22px] font-bold">Say hello</h2>
          <p className="text-[15px] text-sys-secondary">Questions, bugs or ideas for either app.</p>
        </div>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="rounded-xl bg-sys-label px-5 py-3 text-[15px] font-semibold text-white dark:text-black"
        >
          {SUPPORT_EMAIL}
        </a>
      </section>
    </main>
  </LabsShell>
);

export default LabsPage;
